import type { CustomerOption } from '@invoice/shared/schemas';
import { Loader2, Search, X } from 'lucide-react';
import { type ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCustomerList } from '@/lib/queries/customers';
import { cn } from '@/lib/utils';

interface CustomerLookupFieldProps {
  label: string;
  inputId?: string | undefined;
  placeholder?: string | undefined;
  idleHint?: string | undefined;
  loadingText?: string | undefined;
  emptyText?: string | undefined;
  selectedPrefix?: string | undefined;
  clearAriaLabel?: string | undefined;
  selectedCustomerId?: string | null | undefined;
  selectedCustomerName?: string | null | undefined;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  errorMessage?: string | undefined;
  inputTestId?: string | undefined;
  resultsTestId?: string | undefined;
  onSelect: (customer: CustomerOption | null) => void;
}

export function CustomerLookupField({
  label,
  inputId,
  placeholder = 'Kundenname in SAP suchen',
  idleHint = 'Mindestens 2 Zeichen eingeben, um SAP-Kunden zu suchen.',
  loadingText = 'SAP-Kunden werden gesucht...',
  emptyText = 'Keine SAP-Kunden gefunden.',
  selectedPrefix = 'Ausgewählt',
  clearAriaLabel,
  selectedCustomerId,
  selectedCustomerName,
  disabled = false,
  required = false,
  errorMessage,
  inputTestId,
  resultsTestId,
  onSelect,
}: CustomerLookupFieldProps) {
  const [search, setSearch] = useState(selectedCustomerName ?? '');
  const [searchDirty, setSearchDirty] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (selectedCustomerId && selectedCustomerName) {
      setSearch(selectedCustomerName);
      setSearchDirty(false);
      return;
    }

    if (!selectedCustomerId && !selectedCustomerName && !searchDirty) {
      setSearch('');
    }
  }, [searchDirty, selectedCustomerId, selectedCustomerName]);

  const normalizedSearch = search.trim();
  const shouldSearch = !disabled && focused;
  const querySearch = searchDirty && normalizedSearch.length > 0 ? normalizedSearch : undefined;

  const customersQuery = useCustomerList({
    search: shouldSearch ? querySearch : undefined,
    enabled: shouldSearch,
  });

  const results = customersQuery.data?.items ?? [];
  const resultsVisible = shouldSearch;
  const selectedSummary = useMemo(() => {
    if (!selectedCustomerId) return null;
    const visibleName =
      selectedCustomerName && selectedCustomerName.length > 0 ? selectedCustomerName : search;
    if (visibleName.length > 0) {
      return `${visibleName} (${selectedCustomerId})`;
    }
    return selectedCustomerId;
  }, [search, selectedCustomerId, selectedCustomerName]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setSearch(nextValue);
    setSearchDirty(true);

    if (selectedCustomerId || selectedCustomerName) {
      onSelect(null);
    }
  };

  const handleSelect = (customer: CustomerOption) => {
    setSearch(customer.name);
    setSearchDirty(false);
    onSelect(customer);
  };

  const handleClear = () => {
    setSearch('');
    setSearchDirty(false);
    onSelect(null);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId}>
        {label}
        {required ? ' *' : ''}
      </Label>

      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={inputId}
          value={search}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          aria-invalid={errorMessage ? true : undefined}
          aria-label={label}
          disabled={disabled}
          data-testid={inputTestId}
          className="pl-9 pr-11"
        />
        {search.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            onMouseDown={(event) => event.preventDefault()}
            disabled={disabled}
            aria-label={clearAriaLabel ?? `${label} leeren`}
            className="absolute right-1 top-1/2 size-8 -translate-y-1/2"
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>

      {selectedSummary && !searchDirty ? (
        <p className="text-xs text-muted-foreground">
          {selectedPrefix}: {selectedSummary}
        </p>
      ) : null}

      {resultsVisible ? (
        <div
          className={cn(
            'rounded-md border border-border bg-card',
            customersQuery.isPending ? 'p-3' : 'max-h-56 overflow-y-auto',
          )}
          data-testid={resultsTestId}
        >
          {customersQuery.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {loadingText}
            </div>
          ) : results.length > 0 ? (
            <ul className="divide-y divide-border">
              {results.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(customer)}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-muted"
                    data-testid={`customer-lookup-option-${customer.id}`}
                  >
                    <span className="font-medium text-foreground">{customer.name}</span>
                    <span className="text-xs text-muted-foreground">{customer.id}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-3 text-sm text-muted-foreground">{emptyText}</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{idleHint}</p>
      )}

      {errorMessage ? (
        <p className="text-xs text-destructive" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
